import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Target, Heart, Zap, TrendingUp, Users, Gamepad2, BookOpen, Trophy } from "lucide-react";

/**
 * WhoWeAre Page Component
 * 
 * Comprehensive page explaining Centse's mission, vision, and approach to financial education.
 * Features research-backed statistics and our unique gamification methodology.
 * 
 * Key sections:
 * - Hero section with main mission
 * - Financial literacy crisis statistics
 * - Our solution and approach
 * - Core values and principles
 * - Team/company information
 * 
 * @returns {JSX.Element} Who We Are page component
 */
const WhoWeAre = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
            <div className="text-2xl font-bold text-primary">Centse</div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-16">
        {/* Hero Section */}
        <section className="text-center space-y-6">
          <div className="space-y-4">
            <Badge variant="secondary" className="text-sm font-medium">
              Making Financial Literacy Addictive, Not Fearful
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              We're Building the Future of
              <br />
              Financial Education
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              At Centse, we believe financial literacy shouldn't be intimidating or boring. 
              We're transforming how Gen Z learns about money through gamification, 
              making every cent count and every lesson stick.
            </p>
          </div>
        </section>

        {/* The Problem Section */}
        <section className="space-y-8">
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              The Financial Literacy Crisis is Real
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The statistics are alarming, but they tell a story we're determined to change.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* US Statistics */}
            <Card className="bg-surface border-border hover:shadow-lg transition-shadow">
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="h-6 w-6 text-destructive" />
                </div>
                <CardTitle className="text-3xl font-bold text-destructive">38%</CardTitle>
                <Badge variant="secondary" className="mt-2">United States</Badge>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-muted-foreground">
                  Gen Z financial literacy rate - the lowest among all generations
                </p>
              </CardContent>
            </Card>

            {/* UK Statistics */}
            <Card className="bg-surface border-border hover:shadow-lg transition-shadow">
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-6 w-6 text-destructive" />
                </div>
                <CardTitle className="text-3xl font-bold text-destructive">47%</CardTitle>
                <Badge variant="secondary" className="mt-2">United Kingdom</Badge>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-muted-foreground">
                  Adults scoring 5+ out of 7 on financial knowledge questions vs. OECD average of 56%
                </p>
              </CardContent>
            </Card>

            {/* Global Statistics */}
            <Card className="bg-surface border-border hover:shadow-lg transition-shadow">
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="h-6 w-6 text-destructive" />
                </div>
                <CardTitle className="text-3xl font-bold text-destructive">18%</CardTitle>
                <Badge variant="secondary" className="mt-2">Global Average</Badge>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-muted-foreground">
                  15-year-olds scoring at low performance level on PISA 2022 financial literacy assessment
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-accent/5 border-accent/20">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Zap className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-2">The Educational Gap</h3>
                  <p className="text-muted-foreground">
                    Traditional financial education has failed because it treats money as a scary, 
                    complex topic. Students disengage, retain little, and graduate financially unprepared. 
                    Meanwhile, college graduates score 63% vs. 35% for high school graduates - 
                    showing education works, but we need better methods.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Our Solution Section */}
        <section className="space-y-8">
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              Our Gamification Revolution
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Research proves gamification can increase learning engagement by up to 90% 
              and improve knowledge retention by 75%. We're applying this science to financial education.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-primary/5 border-primary/20">
              <CardHeader>
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mb-4">
                  <Gamepad2 className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-xl">Game-Based Learning Impact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Learning Engagement</span>
                    <span className="font-semibold text-primary">+90%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div className="bg-primary h-2 rounded-full" style={{ width: '90%' }}></div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Knowledge Retention</span>
                    <span className="font-semibold text-primary">+75%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div className="bg-primary h-2 rounded-full" style={{ width: '75%' }}></div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Completion Rates</span>
                    <span className="font-semibold text-primary">+60%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div className="bg-primary h-2 rounded-full" style={{ width: '60%' }}></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-surface border-border">
              <CardHeader>
                <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center mb-4">
                  <Trophy className="h-6 w-6 text-secondary" />
                </div>
                <CardTitle className="text-xl">Research-Backed Results</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  A multi-country randomized controlled trial showed game-based financial education 
                  improved students' financial literacy by <strong className="text-foreground">0.313 standard deviations</strong> - 
                  a statistically significant improvement.
                </p>
                <p className="text-muted-foreground">
                  Studies consistently show gamification triggers dopamine release, 
                  creating positive associations with learning rather than anxiety and avoidance.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Our Values Section */}
        <section className="space-y-8">
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              Our Core Values
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything we build is guided by these fundamental principles.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-surface border-border text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">Accessibility First</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Financial education should be available to everyone, regardless of background, 
                  income, or starting knowledge level. We break down barriers, not build them.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-surface border-border text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-16 h-16 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8 text-secondary" />
                </div>
                <CardTitle className="text-xl">Empowerment Over Fear</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We transform financial anxiety into financial confidence. 
                  Every interaction builds competence and self-efficacy, not intimidation.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-surface border-border text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="h-8 w-8 text-accent" />
                </div>
                <CardTitle className="text-xl">Progress Through Play</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Learning happens best when it's engaging and fun. 
                  We use game mechanics to create intrinsic motivation and lasting behavioral change.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Mission Statement */}
        <section className="text-center space-y-6">
          <Card className="bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10 border-primary/20">
            <CardContent className="pt-8 pb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-muted-foreground max-w-4xl mx-auto leading-relaxed">
                To democratize financial literacy by making it as engaging and addictive as 
                the best mobile games. We're building a generation that doesn't fear money management 
                but sees it as an exciting challenge to master. Because when financial education 
                is fun, retention soars, confidence builds, and real-world impact follows.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* CTA Section */}
        <section className="text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">
            Ready to Transform Your Financial Future?
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Join thousands of users who are already making financial literacy fun, 
            engaging, and effective.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate("/signup")}
              className="bg-gradient-to-r from-primary via-primary/90 to-primary hover:opacity-90 transition-all duration-300"
            >
              Start Your Journey
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate("/how-it-works")}
              className="border-border hover:bg-muted/50"
            >
              See How It Works
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default WhoWeAre;